<script setup lang="ts">
import { cn } from '@ui/lib/utils.js';
import { SwitchRoot, type SwitchRootEmits, type SwitchRootProps, SwitchThumb, useForwardPropsEmits } from 'reka-ui';
import { computed, type HTMLAttributes } from 'vue';
import { pSwitchThumbVariants, pSwitchVariants, type PSwitchVariants } from '@ui/components/core/p-switch/index.js';

export interface PSwitchProps extends SwitchRootProps {
    class?: HTMLAttributes['class'];
    variant?: PSwitchVariants['variant'];
}

const props = defineProps<PSwitchProps>();

const emits = defineEmits<SwitchRootEmits>();

const delegatedProps = computed(() => {
    const { class: _, ...delegated } = props;

    return delegated;
});

const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
    <SwitchRoot
        data-slot="switch"
        v-bind="forwarded"
        :class="
            cn(
                'relative',
                'peer',
                'focus-visible:ring-ring-default',
                'inline-flex',
                'shrink-0',
                'items-center',
                'shadow-xs',
                'transition-all',
                'outline-none',
                'focus-visible:ring-[3px]',
                'disabled:cursor-not-allowed',
                'disabled:opacity-50',
                pSwitchVariants({ variant }),
                props.class,
            )
        "
    >
        <template v-if="variant === 'square'">
            <div class="p-2">
                <div class="size-4 opacity-30 transition-opacity group-data-[state=unchecked]:opacity-100">
                    <slot name="unchecked" />
                </div>
            </div>
            <div class="p-2">
                <div class="size-4 opacity-30 transition-opacity group-data-[state=checked]:opacity-100">
                    <slot name="checked" />
                </div>
            </div>
        </template>
        <SwitchThumb
            data-slot="switch-thumb"
            :class="
                cn('absolute pointer-events-none block ring-0 transition-transform', pSwitchThumbVariants({ variant }))
            "
        >
            <slot name="thumb" />
        </SwitchThumb>
    </SwitchRoot>
</template>
